import React from 'react'
import { Form, InputNumber, Divider } from 'antd'
import styled from '@emotion/styled'
import { FullWidthInputNumber } from 'components/Widgets'

interface CapacityRulesProps {
  orderLimitFieldName: string
  disabled?: boolean
}

const CapacityRules = ({
  orderLimitFieldName,
  disabled = false
}: CapacityRulesProps) => {
  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader>
        <HeaderTitle>
          <span>Capacity rules</span>
        </HeaderTitle>
        <HeaderDescription>
          <span>
            Set rules to control how much a timeslot can accept. Rules are
            configured per fulfilment type and apply at checkout.
          </span>
        </HeaderDescription>
      </SectionHeader>

      <CapacityCard>
        <CardTitle>
          <span>Order Limit</span>
        </CardTitle>
        <CardDescription>
          <span>Limits the total number of orders accepted in a slot.</span>
        </CardDescription>
        <Form.Item
          name={orderLimitFieldName}
          style={{ marginBottom: 0 }}
          rules={[
            {
              validator: (_, val) => {
                if (val === null || val === undefined) return Promise.resolve()
                if (val === 0)
                  return Promise.reject(new Error('Order limit cannot be 0'))
                if (!Number.isInteger(val))
                  return Promise.reject(new Error('Decimals are not allowed'))
                return Promise.resolve()
              }
            }
          ]}
        >
          <FullWidthInputNumber
            placeholder='Leave empty for unlimited'
            min={1}
            precision={0}
            controls={false}
            disabled={disabled}
            data-testid='capacity-rules-order-limit'
          />
        </Form.Item>
      </CapacityCard>

      <CapacityCard>
        <CardTitle>
          <span>Number of products per timeslot</span>
        </CardTitle>
        <CardDescription>
          <span>
            Limit the items sold per timeslot. Products can be weighted by the
            capacity that they use, for example a pizza counts as 1 and a drink
            as 0 (no limit). Default values can be overridden in your product
            list.
          </span>
        </CardDescription>
        <Form.Item
          name='products_per_timeslot'
          style={{ marginBottom: 0 }}
          rules={[
            {
              validator: (_, val) => {
                if (val === null || val === undefined) return Promise.resolve()
                if (val === 0)
                  return Promise.reject(
                    new Error('Value cannot be 0 — leave empty for no limit')
                  )
                if (!Number.isInteger(val))
                  return Promise.reject(new Error('Decimals are not allowed'))
                return Promise.resolve()
              }
            }
          ]}
        >
          <FullWidthInputNumber
            placeholder='Leave empty for unlimited'
            min={1}
            precision={0}
            controls={false}
            disabled={disabled}
            data-testid='capacity-rules-products-per-timeslot'
          />
        </Form.Item>

        <Divider style={{ margin: '12px 0' }} />

        <SubRow>
          <SubRowText>
            <SubRowLabel>
              <span>
                Capacity used per <strong>product</strong>
              </span>
            </SubRowLabel>
            <SubRowDesc>
              <span>
                How much each product counts toward the limit per timeslot.
              </span>
            </SubRowDesc>
          </SubRowText>
          <Form.Item
            name='capacity_per_product'
            style={{ marginBottom: 0, marginLeft: 12 }}
            rules={[
              {
                validator: (_, val) => {
                  if (val === null || val === undefined)
                    return Promise.resolve()
                  if (!Number.isInteger(val))
                    return Promise.reject(new Error('Decimals are not allowed'))
                  return Promise.resolve()
                }
              }
            ]}
          >
            <InputNumber
              min={0}
              precision={0}
              style={{ width: 80 }}
              placeholder='Amount'
              disabled={disabled}
              data-testid='capacity-rules-capacity-per-product'
            />
          </Form.Item>
        </SubRow>

        <SubRow isLast>
          <SubRowText>
            <SubRowLabel>
              <span>
                Capacity used per <strong>modifier</strong>
              </span>
            </SubRowLabel>
            <SubRowDesc>
              <span>
                How much each modifier counts toward the limit per timeslot.
              </span>
            </SubRowDesc>
          </SubRowText>
          <Form.Item
            name='capacity_per_modifier'
            style={{ marginBottom: 0, marginLeft: 12 }}
            rules={[
              {
                validator: (_, val) => {
                  if (val === null || val === undefined)
                    return Promise.resolve()
                  if (!Number.isInteger(val))
                    return Promise.reject(new Error('Decimals are not allowed'))
                  return Promise.resolve()
                }
              }
            ]}
          >
            <InputNumber
              min={0}
              precision={0}
              style={{ width: 80 }}
              placeholder='Amount'
              disabled={disabled}
              data-testid='capacity-rules-capacity-per-modifier'
            />
          </Form.Item>
        </SubRow>
      </CapacityCard>
    </div>
  )
}

const SectionHeader = styled.div`
  margin-bottom: 16px;
`

const CapacityCard = styled.div`
  border: 1px solid #d2d2d9;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 16px;
`

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
`

const CardDescription = styled.div`
  font-size: 12px;
  color: #77778e;
  margin-bottom: 12px;
`

const SubRow = styled.div<{ isLast?: boolean }>(
  ({ isLast = false }) => `
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  ${isLast ? 'margin-bottom: 0;' : ''}
`
)

const SubRowText = styled.div`
  flex: 1;
`

const SubRowLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
  margin-bottom: 2px;
`

const SubRowDesc = styled.div`
  font-size: 12px;
  color: #77778e;
`

const HeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #4a4a68;
`

const HeaderDescription = styled.div`
  font-size: 12px;
  color: #77778e;
`

export default CapacityRules
